import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { ProjectApi, TimeLogApi } from '../../api';
import { useContainer } from '../useContainer';
import { ProjectAssigneeRateRowDto } from '../../commonTypes/project.types';
import { TimeLogDto } from '../../commonTypes/timelog.types';
import { aggregateTimelogsIntoRateBuckets } from '../../util/timesheet/attributeTimelogsToAssigneeRateRows';
import { dateTimeToApiCalendarDate } from '../../util/timesheet/apiPeriodIso';

type UseProjectTimesheetAttributionParams = {
    projectId: string;
    periodStart: DateTime;
    periodEnd: DateTime;
    enabled: boolean;
    onlyUserSub?: string;
    revision?: number;
};

export const useProjectTimesheetAttribution = ({
    projectId,
    periodStart,
    periodEnd,
    enabled,
    onlyUserSub,
    revision = 0,
}: UseProjectTimesheetAttributionParams) => {
    const projectApi = useContainer(ProjectApi);
    const timeLogApi = useContainer(TimeLogApi);

    const [rateRows, setRateRows] = useState<ProjectAssigneeRateRowDto[]>([]);
    const [timelogs, setTimelogs] = useState<TimeLogDto[]>([]);
    const [ratesReady, setRatesReady] = useState(() => !enabled);
    const [logsReady, setLogsReady] = useState(() => !enabled);

    const fromDate = dateTimeToApiCalendarDate(periodStart);
    const toDate = dateTimeToApiCalendarDate(periodEnd);

    useEffect(() => {
        if (!enabled) {
            setRatesReady(true);
            setLogsReady(true);
            setRateRows([]);
            setTimelogs([]);
            return;
        }
        if (!fromDate || !toDate) {
            setRatesReady(true);
            setLogsReady(true);
            return;
        }

        setRatesReady(false);
        const { exec, abort } = projectApi.postProjectAssigneesRateForPeriod({
            body: { from: fromDate, to: toDate, projectId },
            onSuccess: res => setRateRows(res.body ?? []),
            onError: () => setRateRows([]),
            onFinally: () => setRatesReady(true),
        });
        exec();
        return abort;
    }, [enabled, projectId, fromDate, toDate, revision, projectApi]);

    useEffect(() => {
        if (!enabled) {
            setLogsReady(true);
            setTimelogs([]);
            return;
        }
        if (!fromDate || !toDate) {
            setLogsReady(true);
            return;
        }

        setLogsReady(false);
        if (onlyUserSub) {
            const { exec, abort } = timeLogApi.getUserTimelog({
                query: {
                    from: fromDate,
                    to: toDate,
                    projectId,
                    userSub: onlyUserSub,
                },
                onSuccess: res => setTimelogs(res.body ?? []),
                onError: () => setTimelogs([]),
                onFinally: () => setLogsReady(true),
            });
            exec();
            return abort;
        }

        const { exec, abort } = timeLogApi.getTimelogsByProject({
            query: {
                from: fromDate,
                to: toDate,
                projectId,
            },
            onSuccess: res => setTimelogs(res.body ?? []),
            onError: () => setTimelogs([]),
            onFinally: () => setLogsReady(true),
        });
        exec();
        return abort;
    }, [enabled, projectId, fromDate, toDate, onlyUserSub, revision, timeLogApi]);

    const buckets = useMemo(() => {
        if (!enabled || !ratesReady || !logsReady) return new Map();
        return aggregateTimelogsIntoRateBuckets(timelogs, rateRows, projectId, periodStart, periodEnd);
    }, [enabled, ratesReady, logsReady, timelogs, rateRows, projectId, periodStart, periodEnd]);

    const isReady = !enabled || (ratesReady && logsReady);

    return { rateRows, timelogs, buckets, isReady };
};
